import React, { useMemo } from "react";
import { Formik } from "formik";
import {
  AccessControl,
  Button,
  ErrorMessageField,
  Icon,
  Input,
  PhoneInput,
  SelectPickerInput,
} from "@shared/components";
import { Hospital, Role, User } from "@shared/models";
import { CreateUserDto } from "@containers/User/interfaces";
import { PERMISSION } from "@shared/constants";
import { useCheckPermission } from "@shared/hooks";

import { getInitValues, getValidationSchema, prepareSubmitValue } from "./formHelpers";

import "./index.scss";

interface UserFormProps {
  user: User | null;
  hospital: Hospital | null;
  authorizedUser: User | null;
  roles: Role[];
  onChangeForm: () => void;
  onChangeStatus: (userId: number) => void;
  onCloseForm: () => void;
  onSubmitForm: (payload: CreateUserDto) => void;
}

const UserForm: React.FunctionComponent<UserFormProps> = (props) => {
  const { user, roles, onChangeForm, onChangeStatus, onCloseForm, onSubmitForm, authorizedUser, hospital } = props;

  const canCreateEditUser = useCheckPermission(user ? PERMISSION.EDIT_USER : PERMISSION.CREATE_USER);

  const formValues = useMemo(() => getInitValues(user, hospital?.id), [user, hospital?.id]);
  const validationSchema = useMemo(() => getValidationSchema(user), [user]);

  const rolesOptions = useMemo(
    () =>
      roles.map((r) => ({
        label: r.name,
        value: String(r.id),
      })),
    [roles],
  );

  return (
    <Formik
      validationSchema={validationSchema}
      enableReinitialize={true}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        onSubmitForm(prepareSubmitValue(values));
        setSubmitting(false);
      }}
      initialValues={formValues}
      validate={() => {
        onChangeForm();
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
        <div className="user-form">
          <form onSubmit={handleSubmit}>
            <div className="user-form-header">
              <div className="user-form-header-left">{user ? "Edit User" : "New User"}</div>
              <div className="user-form-header-right">
                <AccessControl permission={user ? PERMISSION.EDIT_USER : PERMISSION.CREATE_USER}>
                  <Button type="submit" className="action-btn">
                    {user ? "Save" : "Create"}
                  </Button>
                </AccessControl>
                <Button className="close-btn" onClick={() => onCloseForm()}>
                  <Icon type="clear" />
                </Button>
              </div>
            </div>
            <div className="user-form-content">
              <div className="user-name-row">
                <div className="first-name-column">
                  <Input
                    disabled={!canCreateEditUser}
                    type="text"
                    labelTitle="First Name*"
                    name="first_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.first_name}
                    placeholder="First Name"
                  />
                  <ErrorMessageField name="first_name" />
                </div>
                <div className="last-name-column">
                  <Input
                    disabled={!canCreateEditUser}
                    type="text"
                    labelTitle="Last Name*"
                    name="last_name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.last_name}
                    placeholder="Last Name"
                  />
                  <ErrorMessageField name="last_name" />
                </div>
              </div>
              <div className="email-row">
                <Input
                  type="email"
                  labelTitle="Email*"
                  name="login"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.login}
                  placeholder="Email"
                  disabled={!!user || !canCreateEditUser}
                />
                <ErrorMessageField name="login" />
              </div>
              <div className="role-row">
                <SelectPickerInput
                  disabled={!canCreateEditUser}
                  name="role_id"
                  data={rolesOptions}
                  className="role-input"
                  onChange={(value) => setFieldValue("role_id", value)}
                  onClean={handleChange}
                  labelTitle="Role*"
                  value={values.role_id}
                  placeholder="Select Role*"
                />
                <ErrorMessageField name="role_id" />
              </div>
              <div className="phone-row">
                <PhoneInput
                  labelTitle="Phone*"
                  disabled={!canCreateEditUser}
                  onChange={(v: string) => setFieldValue("phone", v)}
                  onBlur={handleBlur}
                  value={values.phone || ""}
                  placeholder="15055555555"
                />

                <ErrorMessageField name="phone" />
              </div>
            </div>
          </form>
          <AccessControl permission={PERMISSION.DELETE_USER}>
            {user && authorizedUser && authorizedUser.id !== user.id ? (
              <div className="user-form-footer">
                <Button
                  className={user.is_active ? "deactivate" : "activate"}
                  onClick={() => onChangeStatus(Number(user.id))}
                >
                  {user.is_active ? "Deactivate" : "Activate"}
                </Button>
              </div>
            ) : null}
          </AccessControl>
        </div>
      )}
    </Formik>
  );
};

export default UserForm;
